import {
    AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef,
    effect,
    ElementRef,
    HostListener, inject, Injector, Signal, signal, untracked, viewChild,
    ViewContainerRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import type { AgGridAngular } from 'ag-grid-angular';
import type {
    CellSelectionChangedEvent,
    ColDef,
    ColumnResizedEvent,
    IRowNode,
    RowNode,
    RowSelectedEvent,
    ScrollState,
    SelectionChangedEvent
} from 'ag-grid-enterprise';
import { tap, timer } from 'rxjs';
import { DataAggregation } from 'src/app/pbir/dataModel';
import { debounced } from 'src/app/pbir/utils/debounced';
import { logger } from 'src/app/pbir/utils/logger';
import { SortDirection, UserVisualStateDto } from 'src/app/services/api.services';
import { VisualComponent } from '../../base/Visual.component';
import { Visual } from '../../Visual';
import { Serialize } from '../../VisualDataSerializer';
import { VisualField } from '../../VisualField';
import { VisualFieldRoleType } from '../../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../../VisualFieldRoleTypeMap';
import { VisualFieldSorting } from '../../VisualFieldSorting';
import { VisualHost } from '../../VisualHost';
import { VisualStyle } from '../../VisualStyle';
import { VisualType } from '../../VisualType';
import * as AgGridConstants from '../shared';
import { AgGridPivotTableStyleEditorComponent } from '../styleEditor/TableStyleEditorComponent';
import { Pivot, PivotGroup, PivotRow } from './pivot';
import { AgGridController } from './agController';
import { DataPoint, DataPointField } from '../../VisualInteractions';

const LOGGER = logger('AgGridPivotTableVisualComponent');

const ROWHEADER_COLID = 'ag-Grid-AutoColumn';
const ROWHEADER_MIN_W = 200;

@Component({
  selector: 'pbir-visual__ag-grid__pivot-table',
  templateUrl: './PivotTableVisual.component.html',
  styleUrls: ['../styles.scss', './PivotTableVisual.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[style.--ag-grid-table-header-height]': 'tableLayout().headerHeightPx',
    '[style.--ag-grid-table-row-height]': 'tableLayout().rowHeightPx',
    '[style.--ag-grid-table-grand-total-row-height]': 'tableLayout().grandTotalRowHeightPx',

    '[style.--ag-grid-sort-indicator-font-size]': 'tableLayout().sortIndicatorFontSize',
    '[style.--ag-grid-sort-indicator-size]': 'tableLayout().sortIndicatorSizePx',
    '[style.--ag-grid-pivot-column-count]': 'columnFields().length',
  }
})
export class AgGridPivotTableVisualComponent 
  extends VisualComponent<
    AgGridPivotTableVisual.Options, 
    AgGridPivotTableVisual.State,
    AgGridPivotTableVisual.Style,
    VisualComponent.DefaultData> {
  readonly AgGridConstants = AgGridConstants;

  readonly injector = inject(Injector);
  readonly viewContainerRef = inject(ViewContainerRef);
  readonly destroyRef = inject(DestroyRef);
  
  readonly tableLayout = computed(() => this.style()?.tableLayout() ?? AgGridConstants.TableLayout.standard);
  readonly pivotColumnDisplay = computed(() => this.style()?.pivotColumnDisplay()?.display ?? 'singleColumn');

  readonly roles = computed(() => {
    return {
      rows:     this.visual()?.fieldRoles.find(AgGridPivotTableVisual.roles.rows),
      columns:  this.visual()?.fieldRoles.find(AgGridPivotTableVisual.roles.columns),
      measures: this.visual()?.fieldRoles.find(AgGridPivotTableVisual.roles.measures),
    };
  });

  readonly rowFields     = computed(() => [...this.roles().rows?.fields.exludingInvalidCalc() ?? []]);
  readonly columnFields  = computed(() => [...this.roles().columns?.fields.exludingInvalidCalc() ?? []]);
  readonly measureFields = computed(() => [...this.roles().measures?.fields.exludingInvalidCalc() ?? []]);
  readonly allFields     = computed(() => [...this.rowFields(), ...this.columnFields(), ...this.measureFields()]);
  
  readonly canRender = computed(() => this.isVisualValid() && this.allFields().length > 0);

  readonly columnOnlyDisplay = computed(() =>
    !this.rowFields().length && this.columnFields().length && !this.measureFields().length);

  readonly hasColumns = computed(() => this.columnFields().length);

  readonly pivot = signal<Pivot>(null);
  readonly pivotUpdate = effect(() => {
    let _ = [this.data.requestIndex(), this.allFields(), this.pivotColumnDisplay()];

    untracked(() => {
      if (this.pivot()) {
        // reset on pivot change
        this.resetOnPivotChange();
      }

      this.pivot.set(new Pivot(this));
      this.ag.reset();
    });
  });

  private resetOnPivotChange() {
    this.rowHeaderResizedByUser.set(false);
    let state = this.state();
    if (state) {
      state.columns.set([]);
      state.scroll.set({ left: 0, top: 0 });
      state.rowHeaderResized.set(false);
      this.broadcastStateChange();
    }
  }


  readonly agGrid = viewChild<AgGridAngular>('agGrid');
  readonly ag = new AgGridController(this, this.agGrid, this.viewContainerRef);

  //#region Grand Total
  readonly grandTotalFields = computed(() =>
    this.allFields().filter(f =>
      f.isAggregated && (
        f.dataType.isNumeric ||
        [DataAggregation.Count, DataAggregation.CountDistinct].includes(f.aggregation)
      )
    )
  );

  readonly hasGrandTotal = computed(() => this.grandTotalFields().length > 0);

  readonly grandTotalColDefs = computed<ReadonlyArray<ColDef>>(() => {
    return [];
    /*
    return this.allFields().map(f => {
      let enabled =
        f.dataType.isNumeric ||
        [DataAggregation.Count, DataAggregation.CountDistinct].includes(f.aggregation);

      if (!enabled) {
        return {
        };
      }

      let cellClass = [
        'ag-grid-grand-totals-cell',
        `ag-grid-cell-data-type-${f.dataType.name}`
      ];

      if (f.dataType.isNumeric)
        cellClass.push('ag-grid-cell-right-align');

      return {
        ...AgGridConstants.grandTotals.ag.columnDef,

        context:     f,
        valueGetter: params => {
          return ''
        },

        cellClass,
        headerClass: cellClass,
        headerTooltip: DataAggregation.Sum.formatAggregationOfText(f.shortTitle),
      };
    });*/
  });
  //#endregion
  
  readonly cellNavigationStarted = signal(false);
  
  startCellNavigation() {
    this.cellNavigationStarted.set(true);
  }


  //#region Selection
  protected override onSelectionChange(e) {
    super.onSelectionChange(e);
  }

  protected override onSelectionCleared(e) {
    untracked(() => {
      super.onSelectionCleared(e);

      if (this.ag?.gridReady()) {
        let api = this.ag.ag().api;
        api.deselectAll();
        api.clearCellSelection();
      }

      this.cellNavigationStarted.set(false);
    });
  }
  
  private hasAnySelection() {
    if (!this.ag.gridReady() || !this.agGrid()?.api)
      return false;
      
    let api = this.agGrid().api;
    return !!api.getSelectedNodes()?.length
        || !!api.getCellRanges()?.length;
  }

  private getSelectedRows(): ReadonlyArray<PivotRow> {
    if (!this.ag.gridReady() || !this.agGrid()?.api)
      return [];

    let api = this.agGrid().api;
      
    let selectedCellRanges = api.getCellRanges() ?? [];

    let rows: PivotRow[] = [];

    let nodesByIndex = new Map<number, IRowNode[]>();
    api.forEachNode(n => {
      let list = nodesByIndex.getOrCreate(n.rowIndex, _ => []);
      list.push(n);
    });

    for (let x of selectedCellRanges) {
      let min = x.startRow.rowIndex;
      let max = x.endRow.rowIndex;
      if (min > max)
        [max, min] = [min, max];

      for (let i = min; i <= max; ++i) {
        let rowsByIndex = nodesByIndex.get(i);
        if (rowsByIndex) {
          for (let r of rowsByIndex)
            rows.push(r.data);
        }
      }
    }

    return rows;
  }

  private updateDataPointSelectionFromAgGrid() {
    untracked(() => {
      if (!this.ag.gridReady() || !this.agGrid()?.api)
        return;

      let dataPoints: DataPoint[] = [];
      let anySelection = false;

      for (let dataRow of this.getSelectedRows()) {
        let dataPointFields: DataPointField[] = [];

        for (let r of dataRow.hierarchy()) {
          dataPointFields.push({
            field: r.ownValue.group.vf,
            value: r.ownValue.raw
          });
        }

        if (!dataPointFields.length)
          continue;

        let dataPoint = this.dataPoints.get(dataPointFields);

        for (let dp = dataPoint; dp.parent; dp = dp.parent) {
          dataPoints.push(dp);
        }

        anySelection = true;
      }

      this.selectionSet(dataPoints);
      this.setNonDataPointSelectionActive(anySelection);
    });
  }

  private readonly updateDataPointSelectionFromAgGridDebounced = debounced(() => {
    this.updateDataPointSelectionFromAgGrid();
  }, 1, this.destroyRef);
  
  onAgGridSelectionChanged(e: SelectionChangedEvent) {
    this.updateDataPointSelectionFromAgGridDebounced();
  }

  onAgGridCellSelectionChanged(e: CellSelectionChangedEvent) {
    if (e.finished)
      this.updateDataPointSelectionFromAgGridDebounced();
  }

  onAgGridRowSelected(e: RowSelectedEvent) {
  }
  //#endregion

  
  readonly rowHeaderResizedByUser = signal(false);
  
  onAgGridColumnResized(e: ColumnResizedEvent) {
    LOGGER.debug('columnResized', e);

    if (e.source === 'uiColumnResized' || e.source === 'autosizeColumns') {
      let autoSized = e.source === 'autosizeColumns';
      if (!autoSized) {
        let colDef = e.column?.getColDef();
        if (colDef.colId == ROWHEADER_COLID) {
          this.rowHeaderResizedByUser.set(true);
          this.state().rowHeaderResized.set(true);
        }
      }

      this.state().columns.set(this.captureColumnsState());
      this.broadcastStateChange();
    }
  }

  readonly rowHeaderMinWidth = computed(() => {
    let baseWidth = ROWHEADER_MIN_W;
    if (this.rowHeaderResizedByUser()) {
      return this.state()?.columns()?.find(x => x.field == ROWHEADER_COLID)?.width ?? baseWidth;
    }

    let deepestExpandedLevel = this.ag.deepestExpandedLevel();
    let indent = (deepestExpandedLevel < 1) ? 0 : (22 + deepestExpandedLevel * 22);

    return baseWidth + indent;
  });

  private readonly resizeRowHeaderOnExpandCollapse = effect(() => {
    let [rowHeaderResizedByUser, minWidth] = [this.rowHeaderResizedByUser(), this.rowHeaderMinWidth()];
    if (rowHeaderResizedByUser)
      return;

    untracked(() => {
      let [gridReady, api] = [this.ag.gridReady(), this.ag.ag()?.api];
      if (!gridReady)
        return;

      api.setColumnWidths([{
        key: ROWHEADER_COLID,
        newWidth: minWidth
      }]);
    });
  });


  @HostListener('window:click', ['$event'])
  @HostListener('window:touchstart', ['$event'])
  @HostListener('window:focus', ['$event'])
  endCellNavigation(e: Event) {
    this.cellNavigationStarted.set(false);
  }

  readonly rendered = signal(false);
  readonly failed   = signal(false);


  private onRenderStart() {
    LOGGER.debug('onRenderStart', { component: this });

    this.rendered.set(false);
    this.failed.set(false);
    
    this.broadcastEvent({ 
      type: VisualHost.EventType.RenderStart
    });
  }

  private onRenderComplete() {
    let error = (this.getError() ?? this.data.contentError());
    let failed = !!error;

    LOGGER.debug('onRenderComplete', { component: this, failed, error });

    this.failed.set(failed);
    this.rendered.set(true);
    
    this.broadcastEvent({ 
      type: VisualHost.EventType.RenderComplete,

      failed,
      error
    });
  }

  private isRenderComplete() {
    if (this.data.contentError())
      return true;

    if (this.canRender()) {
      if (!this.data.loaded())
        return false;

      if (!this.data.success())
        return true;

      if (!this.ag.firstDataRendered())
        return false;

      return true;
    }
    
    return false;
  }

  private getError() {
    let dataError = this.data.error();

    return dataError;
  }

  constructor() {
    super();

    effect(() => {
      let wasRendered = untracked(() => this.rendered());
      let rendered = this.isRenderComplete();

      untracked(() => {
        if (rendered) {
          if (!wasRendered) {
            this.onRenderComplete();
          }
        }
        else {
          if (wasRendered) {
            this.onRenderStart();
          }
        }
      });
    });
    
    let sizingUpdate = debounced(() => {
      if (this.ag.gridReady()) {
        this.ag.applyColumnSizingState();
      }
    }, 50, this.destroyRef);

    effect(() => {
      let _ = [this.ag.gridReady(), this.ag.columnDefs(), this.state()?.columns()];
      untracked(() => sizingUpdate());
    });
    
    effect(() => {
      let _ = this.state()?.columns();
      untracked(() => this.ag.applyColumnSizingState());
    });

    let freezeRows = computed(() => !this.state()?.unfreezeRows());
    effect(() => {
      let [,,v] = [this.ag.gridReady(), this.ag.columnDefs(), freezeRows()];
      untracked(() => this.ag.applyFreezeRowHeaders(v));
    });
    
    effect(() => {
      let [gridReady] = [this.ag.gridReady(), this.ag.columnDefs(), this.state()?.scroll()];
      untracked(() => {
        if (gridReady) {
          this.ag.applyScrollState();
        }
      });
    });

    effect(() => {
      let [rowHeaderResized] = [this.state()?.rowHeaderResized()];
      untracked(() => {
        this.rowHeaderResizedByUser.set(rowHeaderResized);
      });
    });
  }
  

  public captureState(state: AgGridPivotTableVisual.State) {
    super.captureState(state);

    let grid = this.agGrid();
    if (!grid?.api || grid.api.isDestroyed()) {
      LOGGER.error(`Cannot capture state because AgGrid is not bootstrapped.`, { component: this });
      return false;
    }

    state.columns.set(this.captureColumnsState());
    state.scroll.set(this.captureScrollState());
    return true;
  }

  captureColumnsState() {
    let grid = this.agGrid();
    let gridState = grid.api.getState();
    
    let columns = gridState.columnSizing?.columnSizingModel?.map(x => {
      let column = grid.api.getColumnDef(x.colId);
      if (!column)
        return null;

      return {
        field: (column.context as PivotGroup)?.vf?.referenceString() ?? x.colId,
        width: x.width,
        flex: x.flex
      };
    }).filter(x => !!x);

    return columns;
  }

  captureScrollState() {
    let grid = this.agGrid();
    let gridState = grid.api.getState();

    return gridState.scroll;
  }

  
  protected override getClickEventData(e: MouseEvent): VisualHost.ClickEventData {
    let data = super.getClickEventData(e);

    let target = e.target as HTMLElement;
    if (target.closest('.ag-body-vertical-scroll') ||
      target.closest('.ag-body-horizontal-scroll')) {
      data = {
        ...data,
        resetCrossFilter: false
      };

      return data;
    }

    if (!target.closest('.ag-grid-table-data'))
      return data;

    if (target.closest('.ag-header-viewport')) {
      if (!target.closest('.ag-header-cell-menu-button'))
        return data;
    }
    
    if (!target.closest('.ag-row'))
      return data;

    data = {
      ...data,
      action: VisualHost.ClickAction.Selection,
      resetCrossFilter: this.isOtherVisualCrossFilterSource()
    };

    return data;
  }

  
  readonly columnHeaderMenuTriggerElement: Signal<ElementRef<HTMLElement>> = viewChild('columnHeaderMenuTriggerElement', { read: ElementRef });
  readonly columnHeaderMenuTrigger: Signal<MatMenuTrigger> = viewChild<MatMenuTrigger>('columnHeaderMenuTrigger');
  readonly columnHeaderMenuOptions: { title: string, action: () => void }[] = [
    {
      title: 'X',
      action: () => {
      }
    }
  ];


  readonly SortDirection = SortDirection;

  readonly columnHeaderField = signal<VisualField>(null);
  readonly columnHeaderSort = computed(() => {
    let f = this.columnHeaderField();
    if (f?.sorted())
      return f.sortDirection();

    return null;
  });

  openColumnHeaderMenu(f: VisualField, e: MouseEvent) {
    e.preventDefault();
    e.stopImmediatePropagation();

    this.columnHeaderField.set(f);

    let el = this.columnHeaderMenuTriggerElement()?.nativeElement;
    el?.style.setProperty('left', `${e.clientX}px`);
    el?.style.setProperty('top', `${e.clientY}px`);

    timer(0).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this.columnHeaderMenuTrigger()?.openMenu())
    ).subscribe();
  }

  columnHeaderAutoSizeColumn() {
    let api = this.ag.ag().api;
    api.autoSizeColumns(['ag-Grid-AutoColumn']);
  }

  columnHeaderAutoSizeAllColumns() {
    let api = this.ag.ag().api;
    api.autoSizeAllColumns();
  }

  columnHeaderSetSort(sortDirection: SortDirection) {
    this.columnHeaderField().sorted.set(true);
    this.columnHeaderField().sortDirection.set(sortDirection);
    this.reloadPivotData();
  }

  cycleColumnHeaderSort(visualField: VisualField) {
    this.columnHeaderField.set(visualField);
    let isSorted = this.columnHeaderField().sorted();
    let direction = this.columnHeaderField().sortDirection();

    if (isSorted && direction == SortDirection.Descending) {
      this.columnHeaderClearSort();
      return;
    }

    if (isSorted && direction == SortDirection.Ascending) {
      this.columnHeaderSetSort(SortDirection.Descending);
      return;
    }

    this.columnHeaderSetSort(SortDirection.Ascending);
  }

  columnHeaderClearSort() {
    this.columnHeaderField().sorted.set(false);
    this.columnHeaderField().sortDirection.set(null);
    this.reloadPivotData();
  }

  columnHeaderClearAllSort() {
  }

  reloadPivotData() {
    this.pivot.set(new Pivot(this))
    this.agGrid().api.setPivotResultColumns([]);
    this.agGrid().api.refreshServerSide({purge: true});
  }
}

export namespace AgGridPivotTableVisual {
  export const roles = VisualFieldRoleTypeMap.create({
    rows:     new VisualFieldRoleType('rows',     VisualFieldRoleType.Defaults.pivotTableRows),
    columns:  new VisualFieldRoleType('columns',  VisualFieldRoleType.Defaults.pivotTableColumns),
    measures: new VisualFieldRoleType('measures', VisualFieldRoleType.Defaults.pivotTableMeasures),
  });

  export interface ColumnState {
    readonly field: string;
    readonly width?: number;
    readonly flex?: number;
  }

  export class State extends Visual.State {
    @Serialize()
    readonly columns = signal<ReadonlyArray<ColumnState>>([]);

    @Serialize()
    readonly scroll = signal<ScrollState>(null);
    
    @Serialize({ defaultValue: false })
    readonly unfreezeRows = signal<boolean>(null);
    
    @Serialize({ defaultValue: false })
    readonly rowHeaderResized = signal<boolean>(null);

    override setFromUserVisualStateDto(state: Readonly<UserVisualStateDto>) {
      // IN-7423
      // if (!isNullOrUndefined(state?.pivotUnfreezeRows))
      //   this.unfreezeRows.set(state.pivotUnfreezeRows);
    }

    override copyToUserVisualStateDto(state: Readonly<UserVisualStateDto>): UserVisualStateDto {
      return {
        ...super.copyToUserVisualStateDto(state),
        // IN-7423
        // pivotUnfreezeRows: this.unfreezeRows()
      };
    }
  }

  export class Style extends VisualStyle {
    @Serialize(AgGridConstants.TableLayout.serializationOptions)
    readonly tableLayout = signal<AgGridConstants.TableLayout>(AgGridConstants.TableLayout.standard);

    @Serialize(AgGridConstants.PivotColumnDisplay.serializationOptions)
    readonly pivotColumnDisplay = signal<AgGridConstants.PivotColumnDisplay>(AgGridConstants.PivotColumnDisplay.single);
  }
  
  export interface Options extends Visual.Options {
  }

  export const type = new VisualType<Options>('agGridPivotTable', AgGridPivotTableVisualComponent, {
    fieldRoles: roles,
    className: 'agGridPivotTable',
    visualOptions: {
    },
    canReuse: true,
    createState: o => new State(),
    captureState: (state, { host }) => host?.component()?.captureState(state),
    createStyle: o => new Style(),
    styleEditor: AgGridPivotTableStyleEditorComponent,
    fieldSorting: VisualFieldSorting.any(),
    includeSortingInQuery: false,
    loadedWhenRendering: true,
    disableLayoutSorting: true,
    hasBackground: false,
  });
}
